import { useSession } from "@baseline/web/auth";
import { insertAuditLog } from "@baseline/web/auditLog";
import { supabase } from "@baseline/web/supabase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

interface UseLibraryItemParams {
	contentId?: string;
	documentId?: string;
}

export function useLibraryItem({
	contentId,
	documentId,
}: UseLibraryItemParams) {
	if (!contentId && !documentId) {
		throw new Error("useLibraryItem requires contentId or documentId");
	}

	const queryClient = useQueryClient();
	const { data: session } = useSession();
	const navigate = useNavigate();

	const filterColumn = contentId ? "content_id" : "id";
	const filterValue = (contentId ?? documentId)!;
	const queryKeyPrefix = contentId ? "library-item" : "library-item-doc";
	const tagsKeyPrefix = contentId ? "content-tags" : "doc-tags";

	const libraryItemQuery = useQuery({
		queryKey: [queryKeyPrefix, filterValue, session?.user?.id],
		queryFn: async () => {
			const { data } = await supabase
				.from("library_items")
				.select("id, share_token")
				.eq(filterColumn, filterValue)
				.eq("user_id", session!.user.id)
				.eq("is_archived", false)
				.maybeSingle();
			return data;
		},
		enabled: !!session?.user?.id,
	});

	const tagsQuery = useQuery({
		queryKey: [tagsKeyPrefix, filterValue, session?.user?.id],
		queryFn: async () => {
			const { data } = await supabase
				.from("library_items")
				.select(
					`
					id,
					library_item_tags (
						tags (
							id,
							name
						)
					)
				`,
				)
				.eq(filterColumn, filterValue)
				.eq("user_id", session!.user.id)
				.eq("is_archived", false)
				.maybeSingle();

			if (!data) return [];
			const junctionRows = (
				data as unknown as {
					library_item_tags: { tags: { id: string; name: string } }[];
				}
			).library_item_tags;
			return junctionRows.map((row) => row.tags);
		},
		enabled: !!session?.user?.id,
	});

	const updateTitle = useMutation({
		mutationFn: async ({
			libraryItemId,
			newTitle,
			oldTitle,
		}: {
			libraryItemId: string;
			newTitle: string;
			oldTitle: string;
		}) => {
			const { error } = await supabase
				.from("library_items")
				.update({ title: newTitle })
				.eq("id", libraryItemId);
			if (error) throw error;
			if (session?.user?.id) {
				await insertAuditLog({
					user_id: session.user.id,
					action: "update_title",
					entity_type: "library_item",
					entity_id: libraryItemId,
					source: "web",
					changes: { title: { old: oldTitle, new: newTitle } },
				});
			}
		},
		onMutate: async ({ newTitle }) => {
			if (documentId) {
				await queryClient.cancelQueries({
					queryKey: ["document", documentId],
				});
				queryClient.setQueryData(
					["document", documentId],
					(old: Record<string, unknown> | undefined) =>
						old ? { ...old, title: newTitle } : old,
				);
			}
		},
		onSuccess: () => {
			if (session?.user?.id) {
				queryClient.invalidateQueries({
					queryKey: ["library", session.user.id],
				});
			}
			if (documentId) {
				queryClient.invalidateQueries({
					queryKey: ["document", documentId],
				});
			}
		},
	});

	const updateContent = useMutation({
		mutationFn: async ({
			libraryItemId,
			newContent,
		}: {
			libraryItemId: string;
			newContent: string;
		}) => {
			// Read current version to increment it (same pattern as MCP save_doc)
			const { data: current } = await supabase
				.from("library_items")
				.select("version")
				.eq("id", libraryItemId)
				.single();
			const newVersion = (current?.version ?? 0) + 1;

			const { error } = await supabase
				.from("library_items")
				.update({
					content: newContent,
					version: newVersion,
					updated_at: new Date().toISOString(),
				})
				.eq("id", libraryItemId);
			if (error) throw error;
			if (session?.user?.id) {
				await insertAuditLog({
					user_id: session.user.id,
					action: "update",
					entity_type: "library_item",
					entity_id: libraryItemId,
					source: "web",
					changes: { content: newContent },
					metadata: { version: newVersion },
				});
			}
		},
		onMutate: async ({ newContent }) => {
			if (documentId) {
				await queryClient.cancelQueries({
					queryKey: ["document", documentId],
				});
				queryClient.setQueryData(
					["document", documentId],
					(old: Record<string, unknown> | undefined) =>
						old ? { ...old, content: newContent } : old,
				);
			}
		},
		onSuccess: () => {
			if (session?.user?.id) {
				queryClient.invalidateQueries({
					queryKey: ["library", session.user.id],
				});
			}
			if (documentId) {
				queryClient.invalidateQueries({
					queryKey: ["document", documentId],
				});
			}
		},
	});

	const removeFromLibrary = useMutation({
		mutationFn: async (libraryItemId: string) => {
			const { error } = await supabase
				.from("library_items")
				.update({ is_archived: true })
				.eq("id", libraryItemId);
			if (error) throw error;
			if (session?.user?.id) {
				await insertAuditLog({
					user_id: session.user.id,
					action: "archive",
					entity_type: "library_item",
					entity_id: libraryItemId,
					source: "web",
					changes: { is_archived: { old: false, new: true } },
				});
			}
		},
		onSuccess: () => {
			navigate({
				to: "/",
				search: {
					mode: undefined,
					tag: undefined,
					type: undefined,
					summary: undefined,
				},
			});
			if (session?.user?.id) {
				queryClient.invalidateQueries({
					queryKey: ["library", session.user.id],
				});
			}
		},
	});

	return {
		libraryItem: libraryItemQuery.data,
		tags: tagsQuery.data ?? [],
		updateTitle,
		updateContent,
		removeFromLibrary,
	};
}
